import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { Pagination } from "@/components/ui/Pagination";
import { useToast } from "@/features/toast/ToastProvider";
import { formatDate, formatMoney } from "@bookboost/shared/format";

interface DealRow {
  id: string;
  hubspot_deal_id: string;
  deal_name: string | null;
  company_name: string | null;
  owner_id: string | null;
  hubspot_owner_id: string | null;
  amount: number | null;
  currency: string | null;
  stage: string | null;
  stage_changed_at: string | null;
  closed_won_at: string | null;
  onboarding_waived: boolean;
  users: { email: string; full_name: string | null; salary_currency: string | null } | null;
}

interface StageRow {
  stage_id: string;
  stage_label: string;
  pipeline_label: string | null;
}

const dealsSearch = z.object({
  q: z.string().optional(),
  stage: z.string().optional(),
  owner: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});

export const Route = createFileRoute("/admin/deals")({
  validateSearch: dealsSearch,
  component: DealsPage,
});

function DealsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // Debounce the free-text search so we don't refetch on every keystroke (the
  // dropdown/date filters apply immediately — they're discrete choices).
  const [qDraft, setQDraft] = useState(search.q ?? "");
  useEffect(() => {
    setQDraft(search.q ?? ""); // reflect external changes (e.g. Reset) into the input
  }, [search.q]);
  useEffect(() => {
    if (qDraft === (search.q ?? "")) return;
    const t = setTimeout(() => {
      // Updater form merges with the latest params so a concurrent filter change isn't clobbered.
      navigate({ search: (prev) => ({ ...prev, q: qDraft || undefined, page: undefined }) });
    }, 300);
    return () => clearTimeout(t);
  }, [qDraft, search.q, navigate]);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "deals", search],
    queryFn: () => api.get<{ deals: DealRow[]; users: Array<{ id: string; email: string; full_name: string | null }>; stages: StageRow[]; total: number; page: number; pageSize: number }>("/api/admin/deals", { searchParams: search }),
  });

  const setOnboarding = useMutation({
    mutationFn: ({ id, waived }: { id: string; waived: boolean }) =>
      api.post(`/api/admin/deals/${id}/onboarding-waived`, { waived }),
    onSuccess: (_, vars) => {
      toast.ok(vars.waived ? "Marked as free onboarding." : "Unmarked free onboarding.");
      qc.invalidateQueries({ queryKey: ["admin", "deals"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't toggle."),
  });

  // Build the stage dropdown from the cache so labels are human-readable.
  const stageLabel = (id: string | null) =>
    id ? data?.stages.find((s) => s.stage_id === id)?.stage_label ?? id : "—";
  const uniqueStages = [...new Set((data?.deals ?? []).map((d) => d.stage).filter(Boolean))] as string[];

  return (
    <>
      <h1>Deals (closed-won)</h1>

      <Panel>
        <form className="inline filters" style={{ gap: 12, flexWrap: "wrap" }} onSubmit={(e) => e.preventDefault()}>
          <input
            type="search"
            placeholder="Search deal / company / HubSpot id"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            style={{ minWidth: 260, flex: 1 }}
          />
          <select
            value={search.stage ?? ""}
            onChange={(e) => navigate({ search: { ...search, stage: e.target.value || undefined, page: undefined } })}
          >
            <option value="">All stages</option>
            {uniqueStages.map((s) => (
              <option key={s} value={s}>{stageLabel(s)}</option>
            ))}
          </select>
          <select
            value={search.owner ?? ""}
            onChange={(e) => navigate({ search: { ...search, owner: e.target.value || undefined, page: undefined } })}
          >
            <option value="">All owners</option>
            {(data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>{u.full_name ?? u.email}</option>
            ))}
          </select>
          <input
            type="date"
            value={search.from ?? ""}
            onChange={(e) => navigate({ search: { ...search, from: e.target.value || undefined, page: undefined } })}
            aria-label="From"
          />
          <input
            type="date"
            value={search.to ?? ""}
            onChange={(e) => navigate({ search: { ...search, to: e.target.value || undefined, page: undefined } })}
            aria-label="To"
          />
          {Object.keys(search).length > 0 && (
            <button type="button" onClick={() => navigate({ search: {} })}>Reset</button>
          )}
        </form>
        <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>
          {isLoading ? "Loading…" : `${(data?.total ?? 0).toLocaleString()} deal${(data?.total ?? 0) === 1 ? "" : "s"}`}
        </p>
      </Panel>

      <Panel padding="tight">
        {!data || data.deals.length === 0 ? (
          <EmptyState
            icon="deals"
            title={isLoading ? "Loading deals…" : "No deals match"}
            body="Try loosening filters, or reset."
          />
        ) : (
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Closed won</th>
                  <th>Deal</th>
                  <th>Company</th>
                  <th>Owner</th>
                  <th>Stage</th>
                  <th>Amount</th>
                  <th>Free onboarding?</th>
                </tr>
              </thead>
              <tbody>
                {data.deals.map((d) => (
                  <tr key={d.id}>
                    <td data-label="Closed won" className="muted">{formatDate(d.closed_won_at)}</td>
                    <td data-label="Deal">
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <strong>{d.deal_name ?? "—"}</strong>
                        <span className="muted" style={{ fontSize: 11, fontFamily: "var(--font-mono)" }}>
                          {d.hubspot_deal_id}
                        </span>
                      </div>
                    </td>
                    <td data-label="Company">{d.company_name ?? "—"}</td>
                    <td data-label="Owner">{d.users ? d.users.full_name ?? d.users.email : "—"}</td>
                    <td data-label="Stage" className="muted">{stageLabel(d.stage)}</td>
                    <td data-label="Amount">{formatMoney(d.amount, d.currency)}</td>
                    <td data-label="Free onboarding?">
                      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                        <input
                          type="checkbox"
                          checked={d.onboarding_waived}
                          disabled={setOnboarding.isPending}
                          onChange={(e) => setOnboarding.mutate({ id: d.id, waived: e.target.checked })}
                        />
                        {d.onboarding_waived ? "free" : "expected"}
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
        {data && data.total > 0 && (
          <Pagination
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={(p) => navigate({ search: { ...search, page: p } })}
          />
        )}
      </Panel>
    </>
  );
}
